import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Card, Col, Result, Row, Statistic, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import SoftwareImportContext from '@totem/components/devices/software-import/SoftwareImportContext';
import {
  SoftwareImportBuilding,
  SoftwareImportDevice,
  SoftwareImportDeviceAction,
  SoftwareImportIssue,
} from '@totem/components/devices/software-import/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const actionColors: Record<SoftwareImportDeviceAction, string> = {
  created: 'green',
  updated: 'blue',
  failed: 'red',
};

const issueColumns: ColumnsType<SoftwareImportIssue> = [
  {
    title: 'Line',
    dataIndex: 'line',
    key: 'line',
    width: 90,
    sorter: (left, right) => left.line - right.line,
  },
  {
    title: 'Severity',
    dataIndex: 'severity',
    key: 'severity',
    width: 110,
    render: (severity: string) => (
      <Tag color={severity === 'error' ? 'red' : 'gold'}>{severity}</Tag>
    ),
    filters: [
      { text: 'Error', value: 'error' },
      { text: 'Warning', value: 'warning' },
    ],
    onFilter: (value, record) => record.severity === value,
  },
  { title: 'Device', dataIndex: 'device', key: 'device', width: 240 },
  { title: 'Message', dataIndex: 'message', key: 'message' },
];

const deviceColumns: ColumnsType<SoftwareImportDevice> = [
  {
    title: 'Device',
    dataIndex: 'name',
    key: 'name',
    sorter: (left, right) => left.name.localeCompare(right.name),
    render: (name: string, record) =>
      record.id ? (
        <Link to={`/dashboard/devices/${record.id}`}>{name}</Link>
      ) : (
        name
      ),
  },
  {
    title: 'Building',
    dataIndex: 'building',
    key: 'building',
    sorter: (left, right) => left.building.localeCompare(right.building),
  },
  {
    title: 'Operating System',
    dataIndex: 'operatingSystem',
    key: 'operatingSystem',
  },
  {
    title: 'Software',
    dataIndex: 'software',
    key: 'software',
    align: 'right',
    sorter: (left, right) => left.software - right.software,
  },
  {
    title: 'Result',
    dataIndex: 'action',
    key: 'action',
    render: (action: SoftwareImportDeviceAction) => (
      <Tag color={actionColors[action]}>{action}</Tag>
    ),
    filters: [
      { text: 'Created', value: 'created' },
      { text: 'Updated', value: 'updated' },
      { text: 'Failed', value: 'failed' },
    ],
    onFilter: (value, record) => record.action === value,
  },
];

const buildingColumns: ColumnsType<SoftwareImportBuilding> = [
  {
    title: 'Building',
    dataIndex: 'name',
    key: 'name',
    sorter: (left, right) => left.name.localeCompare(right.name),
  },
  {
    title: 'Devices',
    dataIndex: 'devices',
    key: 'devices',
    align: 'right',
    sorter: (left, right) => left.devices - right.devices,
  },
  {
    title: 'Result',
    dataIndex: 'created',
    key: 'created',
    render: (created: boolean) =>
      created ? <Tag color="green">created</Tag> : <Tag>existing</Tag>,
  },
];

const SoftwareImportResults = () => {
  const { fileName, result } = useContext(SoftwareImportContext);
  if (!result) {
    return null;
  }

  const errorCount = result.issues.filter(
    (issue) => issue.severity === 'error',
  ).length;
  const warningCount = result.issues.length - errorCount;
  const hasProblems = errorCount > 0 || result.devicesFailed > 0;

  const issueSummary = [
    errorCount > 0 ? `${errorCount} error(s)` : '',
    warningCount > 0 ? `${warningCount} warning(s)` : '',
  ]
    .filter(Boolean)
    .join(' and ');

  const stats = [
    { title: 'Devices Created', value: result.devicesCreated },
    { title: 'Devices Updated', value: result.devicesUpdated },
    { title: 'Devices Failed', value: result.devicesFailed },
    { title: 'Buildings Created', value: result.buildingsCreated },
    { title: 'Rows Read', value: result.rowsRead },
    { title: 'Rows Skipped', value: result.rowsSkipped },
    { title: 'Software Entries', value: result.softwareEntries },
    { title: 'New Catalog Packages', value: result.packagesCreated },
    { title: 'New Catalog Versions', value: result.versionsAdded },
  ];

  return (
    <>
      <Result
        status={hasProblems ? 'warning' : 'success'}
        title={`${fileName} processed`}
        subTitle={`${result.devicesCreated + result.devicesUpdated} of ${result.devicesInFile} devices imported${
          issueSummary ? ` with ${issueSummary}` : ''
        }.`}
        extra={
          <Link to="/dashboard/software/vulnerabilityReport">
            View Software Vulnerability Report
          </Link>
        }
      />
      <Row gutter={[16, 16]}>
        {stats.map((stat) => (
          <Col key={stat.title} xs={12} sm={8} lg={6} xl={4}>
            <Card size="small">
              <Statistic title={stat.title} value={stat.value} />
            </Card>
          </Col>
        ))}
      </Row>
      <br />
      <Tabs
        defaultActiveKey={result.issues.length > 0 ? 'issues' : 'devices'}
        items={[
          {
            key: 'issues',
            label: `Issues (${result.issues.length})`,
            children: (
              <IBTable
                columns={issueColumns}
                dataSource={result.issues}
                rowKey={(record) =>
                  `${record.line}-${record.device}-${record.message}`
                }
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              />
            ),
          },
          {
            key: 'devices',
            label: `Devices (${result.devices.length})`,
            children: (
              <IBTable
                columns={deviceColumns}
                dataSource={result.devices}
                rowKey={(record) => record.name}
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              />
            ),
          },
          {
            key: 'buildings',
            label: `Buildings (${result.buildings.length})`,
            children: (
              <IBTable
                columns={buildingColumns}
                dataSource={result.buildings}
                rowKey={(record) => record.id}
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default SoftwareImportResults;
